import { createBrowserLogger } from '@zealot/monitoring/browser';

const logger = createBrowserLogger('safeStorage');

type StorageArea = 'local' | 'session';

type SafeStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};

const reportStorageError = (context: string, error: unknown, key?: string): void => {
  logger.error(error instanceof Error ? error : new Error(String(error)), key ? { context, key } : { context });
};

// gloss
const getStorage = (area: StorageArea, onError: (error: unknown) => void): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch (error) {
    onError(error);
    return null;
  }
};

// gloss
const makeSafeStorage = (area: StorageArea): SafeStorage => ({
  getItem: (key: string): string | null => {
    const context = `${area}Storage.getItem`;
    if (typeof window === 'undefined') {
      logger.warn(`Attempted ${context} during SSR`, { key });
      return null;
    }
    try {
      return getStorage(area, (error) => reportStorageError(context, error, key))?.getItem(key) ?? null;
    } catch (error) {
      reportStorageError(context, error, key);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    const context = `${area}Storage.setItem`;
    if (typeof window === 'undefined') {
      logger.warn(`Attempted ${context} during SSR`, { key });
      return;
    }
    try {
      getStorage(area, (error) => reportStorageError(context, error, key))?.setItem(key, value);
    } catch (error) {
      reportStorageError(context, error, key);
    }
  },
  removeItem: (key: string): void => {
    const context = `${area}Storage.removeItem`;
    if (typeof window === 'undefined') {
      logger.warn(`Attempted ${context} during SSR`, { key });
      return;
    }
    try {
      getStorage(area, (error) => reportStorageError(context, error, key))?.removeItem(key);
    } catch (error) {
      reportStorageError(context, error, key);
    }
  },
  clear: (): void => {
    const context = `${area}Storage.clear`;
    if (typeof window === 'undefined') {
      logger.warn(`Attempted ${context} during SSR`);
      return;
    }
    try {
      getStorage(area, (error) => reportStorageError(context, error))?.clear();
    } catch (error) {
      reportStorageError(context, error);
    }
  },
});

export const safeLocalStorage = makeSafeStorage('local');
export const safeSessionStorage = makeSafeStorage('session');
